import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

/**
 * ALGO beside HUMAN, scored by the same measure.
 *
 * <h2>Side by side, never stacked</h2>
 *
 * The whole question is "which of these two did better", and that is a comparison a reader makes
 * by running an eye across a row. Stacked blocks or a tab per book turn it into a memory test,
 * and the figures that differ most are exactly the ones a reader would have to hold in their
 * head while scrolling.
 *
 * <h2>IT COMPUTES NOTHING</h2>
 *
 * Every number arrives from `compareLedgers`, which runs both books through the one reducer in
 * `portfolioMetrics`. A panel that did its own arithmetic for one column is how the Running
 * Ledger and the Simulator came to disagree about Net PnL — the defect this codebase has already
 * paid for once, and it would be far worse here, where the two columns exist precisely to be
 * subtracted from one another.
 */

const GAIN = '#2e7d32';
const LOSS = '#c62828';
const MUTED = 'rgba(255,255,255,0.55)';

/** A ratio that could not be computed. Never a dash that might read as zero. */
const NA = 'n/a';

const fmtPct = (v) => (Number.isFinite(v) ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : NA);
const fmtDd = (v) => (Number.isFinite(v) ? `${v.toFixed(2)}%` : NA);
const fmtRatio = (v) => (Number.isFinite(v) ? v.toFixed(2) : NA);
const fmtInt = (v) => (Number.isFinite(v) ? String(v) : NA);

/**
 * The rows, in the order the brief asked for them.
 *
 * <p>Each carries the sentence a reader needs to not misread it. The `n/a` cases are the ones
 * worth explaining: an absent ratio is an undefined one — a zero denominator — and without the
 * note it reads as a failure to compute rather than as a statement about the book.
 */
export const COMPARISON_ROWS = [
  { key: 'tradeCount', label: 'Trades', fmt: fmtInt, tone: 'none',
    hint: 'Realised trades only. An open discretionary position is a mark, not a result, and is '
      + 'excluded from every figure in this panel.' },
  { key: 'winRatePercent', label: 'Win Rate', fmt: (v) => (Number.isFinite(v) ? `${v.toFixed(1)}%` : NA),
    tone: 'none',
    hint: 'A scratch counts as a loss on both sides, so neither column can be inflated by flat '
      + 'trades.' },
  { key: 'profitFactor', label: 'Profit Factor', fmt: fmtRatio, tone: 'none',
    hint: 'Gross gains over gross losses. n/a means there were no losing trades — an absent '
      + 'denominator, not a perfect score.' },
  { key: 'totalNetPnlPercent', label: 'Gross Return', fmt: fmtPct, tone: 'signed',
    hint: 'COMPOUNDED across trades in the order taken. Not a sum of percentages — adding '
      + 'returns on different bases is not a return.' },
  { key: 'displayDrawdownPercent', label: 'Max Drawdown', fmt: fmtDd, tone: 'drawdown',
    hint: 'Deepest peak-to-trough fall of the trade-sequence equity curve, and the figure Calmar '
      + 'divides by.' },
  { key: 'sharpe', label: 'Sharpe', fmt: fmtRatio, tone: 'signed',
    hint: 'Excess return over annualised volatility, with each TRADE as one period — so a '
      + 'three-week hold and a one-day scalp weigh the same. Not comparable with a fund Sharpe '
      + 'computed from monthly returns. n/a below three trades, or when every return is equal.' },
  { key: 'sortino', label: 'Sortino', fmt: fmtRatio, tone: 'signed',
    hint: 'Excess return over DOWNSIDE deviation only. Higher than Sharpe when the losses are '
      + 'the smaller half. n/a when nothing fell below the hurdle.' },
  { key: 'calmar', label: 'Calmar', fmt: fmtRatio, tone: 'signed',
    hint: 'CAGR over the absolute max drawdown. n/a on a zero drawdown — undefined, not '
      + 'infinite.' },
  { key: 'omega', label: 'Omega', fmt: fmtRatio, tone: 'signed',
    hint: 'Gains above the threshold over losses below it, using the whole distribution rather '
      + 'than its first two moments — which is what a stop-truncated, trail-extended book needs. '
      + 'Above 1 is favourable.' },
  { key: 'informationRatio', label: 'Info Ratio', fmt: fmtRatio, tone: 'signed',
    hint: 'Excess return over a benchmark, divided by tracking error. n/a when no benchmark was '
      + 'supplied — which is not the same as having matched one.' },
];

const toneFor = (tone, value) => {
  if (!Number.isFinite(value)) return MUTED;
  if (tone === 'drawdown') return value > 0 ? LOSS : MUTED;
  if (tone === 'signed') return value > 0 ? GAIN : value < 0 ? LOSS : undefined;
  return undefined;
};

/**
 * Which column won, for the figures where "won" means anything.
 *
 * <p>Only marked when BOTH sides have a number — a column that is `n/a` has not lost, it has not
 * answered, and a tick against the other one would read as a verdict nobody reached. Drawdown is
 * inverted, lower being better; the trade count is not a contest at all.
 */
export function leaderOf(row, algoValue, humanValue) {
  if (row.tone === 'none' && row.key !== 'profitFactor' && row.key !== 'winRatePercent') return null;
  if (!Number.isFinite(algoValue) || !Number.isFinite(humanValue)) return null;
  if (algoValue === humanValue) return null;
  const higherWins = row.key !== 'displayDrawdownPercent';
  const algoBetter = higherWins ? algoValue > humanValue : algoValue < humanValue;
  return algoBetter ? 'algo' : 'human';
}

const Cell = ({ text, colour, bold }) => (
  <Box sx={{
    flex: 1,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    fontFamily: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
    fontSize: 12,
    color: colour || 'inherit',
    fontWeight: bold ? 700 : 400,
  }}>
    {text}
  </Box>
);

Cell.propTypes = {
  text: PropTypes.string.isRequired,
  colour: PropTypes.string,
  bold: PropTypes.bool,
};

const DualLedgerPanel = ({ comparison, sizingModel, openHumanPosition, lastRefusal, mark }) => {
  if (!comparison) return null;
  const { algo, human } = comparison;

  return (
    <Box data-testid="dual-ledger-panel" sx={{ fontSize: 12 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.75 }}>
        <Box sx={{ flex: 1.3 }} />
        <Box sx={{ flex: 1, textAlign: 'right', fontWeight: 700, letterSpacing: 0.4 }}>ALGO</Box>
        <Box sx={{ flex: 1, textAlign: 'right', fontWeight: 700, letterSpacing: 0.4 }}>HUMAN</Box>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', color: MUTED, mb: 1 }}>
        Mechanical baseline · Discretionary copilot
        {sizingModel ? ` · sizing ${sizingModel.replace('_', ' ').toLowerCase()}` : ''}
      </Typography>

      {COMPARISON_ROWS.map((row) => {
        const a = algo[row.key];
        const h = human[row.key];
        const winner = leaderOf(row, a, h);
        return (
          <Tooltip key={row.key} title={row.hint} arrow placement="left">
            <Box sx={{ display: 'flex', alignItems: 'center', py: 0.3 }}>
              <Box sx={{ flex: 1.3, color: MUTED, fontSize: 11, letterSpacing: 0.3 }}>
                {row.label}
              </Box>
              <Cell text={row.fmt(a)} colour={toneFor(row.tone, a)} bold={winner === 'algo'} />
              <Cell text={row.fmt(h)} colour={toneFor(row.tone, h)} bold={winner === 'human'} />
            </Box>
          </Tooltip>
        );
      })}

      {openHumanPosition ? (
        <Typography variant="caption" data-testid="dual-ledger-open"
          sx={{ display: 'block', mt: 1, color: MUTED }}>
          Human holds {openHumanPosition.direction}{' '}
          {Number(openHumanPosition.shares).toFixed(2)} @{' '}
          {Number(openHumanPosition.entryPrice).toFixed(2)}
          {/* THE MARK AT THE PLAYHEAD BAR, which is what the brief's "feed the bar into the state
              machine" amounts to for an open position: the bar moves, so the mark moves.

              It is still EXCLUDED from every figure above, and the sentence says so on the same
              line. On this rule set a single open trade can be worth more than every closed one
              together, so a mark that quietly joined the statistics would not be a rounding
              error — it would be a different number entirely. */}
          {mark ? (
            <span
              data-testid="dual-ledger-mark"
              style={{ color: mark.unrealisedPnl >= 0 ? GAIN : LOSS }}
            >
              {` · ${mark.unrealisedPnl >= 0 ? '+' : ''}${mark.unrealisedPercent.toFixed(2)}%`}
              {` at ${mark.price.toFixed(2)}`}
            </span>
          ) : null}
          {' — excluded from the figures above'}
          {openHumanPosition.sizingNote ? ` · ${openHumanPosition.sizingNote}` : ''}
        </Typography>
      ) : null}

      {lastRefusal ? (
        // A click that did nothing and said nothing is indistinguishable from a broken button.
        <Typography variant="caption" data-testid="dual-ledger-refusal"
          sx={{ display: 'block', mt: 0.5, color: LOSS }}>
          Last action refused: {lastRefusal}
        </Typography>
      ) : null}
    </Box>
  );
};

DualLedgerPanel.propTypes = {
  /** From `compareLedgers` — both books, already scored by the one shared reducer. */
  comparison: PropTypes.shape({
    algo: PropTypes.object,
    human: PropTypes.object,
  }),
  sizingModel: PropTypes.string,
  openHumanPosition: PropTypes.object,
  lastRefusal: PropTypes.string,
  /**
   * From `replayCopilot.markAt` — the open position priced at the PLAYHEAD bar.
   *
   * <p>Computed by the caller, which is the only place that knows which bar the playhead is on.
   * The panel renders it and nothing more: it owns no arithmetic, including this.
   */
  mark: PropTypes.object,
};

DualLedgerPanel.defaultProps = {
  comparison: null,
  sizingModel: null,
  openHumanPosition: null,
  lastRefusal: null,
  mark: null,
};

export default DualLedgerPanel;
